import { useState, type ReactNode } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Paper from '@mui/material/Paper';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import RateReviewIcon from '@mui/icons-material/RateReview';
import BadgeIcon from '@mui/icons-material/Badge';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import HistoryIcon from '@mui/icons-material/History';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import { useOnboardingProgress } from '@/features/onboarding/hooks/useOnboardingProgress';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { NotificationBell } from '@/features/notifications/components/NotificationBell';
import { useGuideWallet } from '@/features/guide-finance/hooks/useGuideFinance';
import { formatCurrency } from '@/features/guide-finance/utils/formatCurrency';
import { useGuidePricing } from '@/features/guide/hooks/useGuidePricing';
import { isGuideMarketplaceLive } from '@/features/guide/utils/pricing';
import { StatusBadge } from '@/shared/components/ui/StatusBadge';
import { GUIDE_ROUTES } from '@/features/guide/constants/routes';
import { BrandLogo } from '@/shared/components/BrandLogo';
import { useGuideDashboardTour } from '@/features/guide/tour/useGuideDashboardTour';
import {
  GUIDE_TOUR_ATTR,
  type GuideTourSlug,
} from '@/features/guide/tour/guideDashboardTour';
import { sidebarDrawerPaperSx, dashboardAppBarSx, dashboardMainSx, dashboardPageSx } from '@/shared/styles/surfaces';
import { authApi } from '@/features/auth/api/auth.api';

const DRAWER_WIDTH = 268;

const navItems: Array<{
  label: string;
  path: string;
  icon: ReactNode;
  tourSlug: GuideTourSlug;
}> = [
  { label: 'Today', path: GUIDE_ROUTES.HOME, icon: <DashboardIcon />, tourSlug: 'today' },
  { label: 'Bookings', path: GUIDE_ROUTES.BOOKINGS, icon: <EventNoteIcon />, tourSlug: 'bookings' },
  {
    label: 'Past trips',
    path: GUIDE_ROUTES.PAST_BOOKINGS,
    icon: <HistoryIcon />,
    tourSlug: 'past-trips',
  },
  { label: 'Pricing', path: GUIDE_ROUTES.PRICING, icon: <AttachMoneyIcon />, tourSlug: 'pricing' },
  {
    label: 'Calendar',
    path: GUIDE_ROUTES.AVAILABILITY,
    icon: <EventAvailableIcon />,
    tourSlug: 'calendar',
  },
  { label: 'Reviews', path: GUIDE_ROUTES.REVIEWS, icon: <RateReviewIcon />, tourSlug: 'reviews' },
  { label: 'Identity', path: GUIDE_ROUTES.IDENTITY, icon: <BadgeIcon />, tourSlug: 'reviews' },
  { label: 'Rewards', path: GUIDE_ROUTES.REWARDS, icon: <EmojiEventsOutlinedIcon />, tourSlug: 'reviews' },
  {
    label: 'Wallet',
    path: GUIDE_ROUTES.WALLET,
    icon: <AccountBalanceWalletIcon />,
    tourSlug: 'wallet',
  },
];

// Rewards stays in the menu; on mobile the home page's Trippy card links to it.
const bottomNavItems = navItems.filter(
  (item) =>
    item.path !== GUIDE_ROUTES.WALLET &&
    item.path !== GUIDE_ROUTES.IDENTITY &&
    item.path !== GUIDE_ROUTES.REWARDS
);

function tourAnchorProps(slug: GuideTourSlug, surface: 'desktop' | 'mobile') {
  return {
    [GUIDE_TOUR_ATTR]: slug,
    [`${GUIDE_TOUR_ATTR}-surface`]: surface,
  } as Record<string, string>;
}

function isNavSelected(path: string, pathname: string) {
  if (path === GUIDE_ROUTES.HOME) return pathname === GUIDE_ROUTES.HOME;
  if (path === GUIDE_ROUTES.PAST_BOOKINGS) return pathname.startsWith(GUIDE_ROUTES.PAST_BOOKINGS);
  if (path === GUIDE_ROUTES.BOOKINGS) {
    return (
      pathname === GUIDE_ROUTES.BOOKINGS ||
      /^\/dashboard\/bookings\/(?!past)[^/]+$/.test(pathname)
    );
  }
  return pathname.startsWith(path);
}

function resolveBottomNav(pathname: string) {
  const match = bottomNavItems.find((item) => isNavSelected(item.path, pathname));
  return match?.path ?? GUIDE_ROUTES.HOME;
}

function GuideNavList({
  onNavigate,
  tourSurface,
}: {
  onNavigate?: () => void;
  /** When set, items become tour targets for that surface (desktop sidebar only). */
  tourSurface?: 'desktop';
}) {
  const location = useLocation();

  return (
    <List sx={{ px: 1.5, py: 1.5, flex: 1 }}>
      {navItems.map((item) => {
        const selected = isNavSelected(item.path, location.pathname);
        return (
          <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              selected={selected}
              onClick={onNavigate}
              {...(tourSurface ? tourAnchorProps(item.tourSlug, tourSurface) : {})}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: selected ? 700 : 500, fontSize: '0.92rem' }}
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}

export function GuideDashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { data: wallet } = useGuideWallet();
  const { data: onboardingProgress } = useOnboardingProgress();
  const { data: pricing, isLoading: pricingLoading } = useGuidePricing();
  useGuideDashboardTour();

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } finally {
      logout();
      navigate('/', { replace: true });
    }
  };

  const profile = onboardingProgress?.profile;
  const displayName =
    profile?.fullName?.trim() || user?.displayName || user?.firstName || user?.phone || 'Guide';
  const avatarSrc = profile?.profilePhoto || user?.avatar;
  const isWalletSection = location.pathname.startsWith(GUIDE_ROUTES.WALLET);
  const currentPage =
    navItems.find((item) => isNavSelected(item.path, location.pathname))?.label ?? 'Host';
  const isLive = isGuideMarketplaceLive(pricing);
  const listingBadgeLabel = pricingLoading
    ? 'Checking listing…'
    : isLive
      ? 'Live listing'
      : 'Add pricing to go live';

  const drawerContent = (tourSurface?: 'desktop') => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2.5, py: 2.5 }}>
        <Box>
          <BrandLogo size="md" surface="dark" />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', display: 'block', mt: 0.75 }}>
            Guide Host
          </Typography>
        </Box>
        <StatusBadge
          status={isLive ? 'approved' : 'pending'}
          label={listingBadgeLabel}
          sx={{
            mt: 1.5,
            bgcolor: isLive ? 'rgba(255,255,255,0.14)' : 'rgba(245, 158, 11, 0.28)',
            color: 'common.white',
            border: '1px solid rgba(255,255,255,0.22)',
            textTransform: isLive ? 'uppercase' : 'none',
            letterSpacing: isLive ? '0.06em' : '0.02em',
            fontSize: isLive ? '0.6875rem' : '0.65rem',
          }}
        />
      </Box>

      <Divider />

      <GuideNavList
        onNavigate={() => setMobileOpen(false)}
        tourSurface={tourSurface}
      />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, ...sidebarDrawerPaperSx },
        }}
      >
        {drawerContent()}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', lg: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, ...sidebarDrawerPaperSx },
        }}
        open
      >
        {drawerContent('desktop')}
      </Drawer>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar position="sticky" elevation={0} sx={dashboardAppBarSx}>
          <Toolbar sx={{ gap: 1.5, minHeight: { xs: 56, sm: 64 } }}>
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(true)}
              sx={{ display: { lg: 'none' } }}
              aria-label="Open menu"
            >
              <MenuIcon />
            </IconButton>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {currentPage}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                Hosting dashboard
              </Typography>
            </Box>

            <Paper
              component={RouterLink}
              to={GUIDE_ROUTES.WALLET}
              elevation={0}
              {...tourAnchorProps('wallet', 'mobile')}
              sx={{
                display: { xs: 'flex', md: 'flex' },
                alignItems: 'center',
                gap: 1.25,
                px: { xs: 1, md: 1.5 },
                py: 1,
                textDecoration: 'none',
                color: 'inherit',
                border: '1px solid',
                borderColor: isWalletSection ? 'primary.main' : 'divider',
                borderRadius: 3,
                minWidth: { xs: 0, md: 170 },
                maxWidth: { xs: 140, md: 'none' },
                bgcolor: isWalletSection ? 'rgba(46, 125, 50, 0.08)' : 'background.paper',
                boxShadow: isWalletSection ? '0 10px 24px rgba(46, 125, 50, 0.12)' : 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: '0 10px 24px rgba(46, 125, 50, 0.12)',
                },
              }}
            >
              <Box
                sx={{
                  width: { xs: 28, md: 36 },
                  height: { xs: 28, md: 36 },
                  borderRadius: 2.5,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AccountBalanceWalletIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: { xs: 'none', sm: 'block' } }}
                >
                  Available balance
                </Typography>
                <Typography variant="body2" fontWeight={800} noWrap>
                  {wallet ? formatCurrency(wallet.availableBalance, wallet.currency) : '…'}
                </Typography>
              </Box>
            </Paper>

            <NotificationBell />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                src={avatarSrc}
                sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: '0.85rem' }}
              >
                {displayName[0]?.toUpperCase()}
              </Avatar>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Typography variant="body2" fontWeight={600} lineHeight={1.2} noWrap>
                  {displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Local guide
                </Typography>
              </Box>
              <IconButton onClick={handleLogout} aria-label="Sign out" size="small">
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        <Box
          component="main"
          sx={{
            ...dashboardMainSx,
            px: isWalletSection ? 0 : { xs: 2, sm: 2.5, md: 3 },
            py: { xs: 2, md: 3 },
            pb: { xs: 10, lg: 3 },
          }}
        >
          <Box sx={isWalletSection ? { width: '100%' } : dashboardPageSx}>
            <Outlet />
          </Box>
        </Box>

        <Paper
          elevation={8}
          sx={{
            display: { xs: 'block', lg: 'none' },
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            borderTop: '1px solid',
            borderColor: 'divider',
            borderRadius: 0,
            bgcolor: 'background.paper',
          }}
        >
          <BottomNavigation
            showLabels
            value={resolveBottomNav(location.pathname)}
            onChange={(_, value) => navigate(value)}
            sx={{
              bgcolor: 'background.paper',
              '& .MuiBottomNavigationAction-root': {
                minWidth: 0,
                px: 0.5,
                py: 0.75,
              },
            }}
          >
            {bottomNavItems.map((item) => (
              <BottomNavigationAction
                key={item.path}
                label={item.label}
                value={item.path}
                icon={item.icon}
                {...tourAnchorProps(item.tourSlug, 'mobile')}
              />
            ))}
          </BottomNavigation>
        </Paper>
      </Box>
    </Box>
  );
}
